import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { observer } from 'mobx-react-lite';
import { FormattedMessage, useIntl, defineMessages } from 'react-intl';
import styled from 'styled-components';
import queryString from 'query-string';
import { rgba } from 'polished';
import classNames from 'classnames';

import {
    Form,
    Colours,
    getLanguageFromLocale,
    QuestionCompact,
    timeoutAsPromise,
    PageBehaviourButton,
    useDebouncedCallback,
    IconShare,
    IconCheckmark,
    copyToClipboard,
    useIsMounted,
} from '@ratehub/base-ui';
import { PROVINCES } from '@ratehub/documents/common/stable';

import COMMON_MESSAGES from '../definitions/Messages';
import CreditCardSynopsisShape from '../definitions/CreditCardSynopsisShape';
import DEFAULT_CARD_PREFERENCES from '../definitions/DefaultCardPreferences';
import fetchCardInfo from '../functions/fetchCardInfo';
import getMonthlySpending from '../functions/getMonthlySpending';
import getSumOfObjectValues from '../functions/getSumOfObjectValues';
import getCreditCardURL, { CC_URL_SLUGS } from '../functions/getCreditCardURL';
import CreditCardFlipContainer from '../components/CreditCardFlipContainer';


const MAX_SIDE_BY_SIDE_LENGTH = 3;


function CCSideBySide({
    initialCardInfos = Array(MAX_SIDE_BY_SIDE_LENGTH).fill(null),
    province = PROVINCES.ONTARIO,
}) {
    const getIsMounted = useIsMounted();
    const intl = useIntl();
    const languageCode = getLanguageFromLocale(intl.locale);
    const [ cardInfoList, setCardInfoList ] = useState(
        // make sure cardInfoList is always of length MAX_SIDE_BY_SIDE_LENGTH
        initialCardInfos.concat(new Array(MAX_SIDE_BY_SIDE_LENGTH - initialCardInfos.length).fill(null))
    );
    const [ monthlySpending, setMonthlySpending ] = useState(() => DEFAULT_CARD_PREFERENCES.monthlySpending);
    const [ monthlySpendingAverage, setMonthlySpendingAverage ] = useState(() => DEFAULT_CARD_PREFERENCES.monthlySpendingAverage);
    const [ hasShared, setHasShared ] = useState(false);
    const [ timeoutId, setTimeoutId ] = useState();


    const pathToCardCompareSPA = getCreditCardURL(languageCode, CC_URL_SLUGS.CARD_COMPARE);
    // update URL if we are on the SPA page whenever cardInfoList changes
    // (only the SPA currently knows how to parse the URL’s search string)
    useEffect(() => {
        if (location.pathname.includes(pathToCardCompareSPA)) {
            window.history.replaceState(null, '', getURLForCardCompareWithIds());
        }
    }, [ cardInfoList ]);

    function getURLForCardCompareWithIds() {
        // get all search string parameters
        const { query } = queryString.parseUrl(location.href);

        // append the cardIdList to the existing search string
        // format: ?cardIds=firstId&?cardIds=secondId&cardIds=thirdId
        const cardIds = cardInfoList.map(card => card?.id).filter(id => id);

        return queryString.stringifyUrl({
            url: location.origin + pathToCardCompareSPA,
            query: {
                ...query,
                cardIds: cardIds,
            },
        });
    }

    // whenever the monthlySpending values change, re-fetch the credit cards to see the updated reward information
    const debouncedFiltersChange = useDebouncedCallback(
        async function handleFiltersChange(values, { isValid }) {
            if (isValid && values.monthlySpendingAverage != null) {
                const {
                    monthlySpending: updatedMonthlySpending,
                    monthlySpendingAverage: updatedMonthlySpendingAverage,
                } = getMonthlySpending(
                    values,
                    { monthlySpending, monthlySpendingAverage }
                );

                // update monthlySpending state and use it for the refetch
                // it will also be used the next time the user finds a card and asks to add it
                setMonthlySpending(updatedMonthlySpending);
                setMonthlySpendingAverage(updatedMonthlySpendingAverage);

                await reFetchCardInfo(updatedMonthlySpending);
            }
        },
        [ monthlySpending, monthlySpendingAverage, cardInfoList, languageCode, province ],
        FILTER_CHANGE_INPUT_DEBOUNCE_DELAY
    );

    async function reFetchCardInfo(updatedMonthlySpending) {
        for (let nIt = 0; nIt < MAX_SIDE_BY_SIDE_LENGTH; ++nIt) {
            const cardId = cardInfoList[nIt]?.id;

            if (cardId) {
                const response = await fetchCreditCardInfo({
                    cardId,
                    languageCode,
                    province,
                    monthlySpending: updatedMonthlySpending,
                });

                if (response) {
                    cardInfoList[nIt] = response;
                }
            }
        }

        setCardInfoList([ ...cardInfoList ]);
    }


    function handleRemoveCard(cardIndex) {
        cardInfoList[cardIndex] = null;
        setCardInfoList([ ...cardInfoList ]);
    }

    async function handleCardSelection(cardId, index) {
        const [ response ] = await Promise.all([
            await fetchCreditCardInfo({
                cardId,
                languageCode,
                province,
                monthlySpending,
            }),

            // provides a pause for the above fetch to happen before flipping this card over
            timeoutAsPromise(500),
        ]);

        if (response) {
            cardInfoList[index] = response;
            setCardInfoList([ ...cardInfoList ]);
        }
    }

    async function shareURL() {
        try {
            await copyToClipboard(getURLForCardCompareWithIds());
            setHasShared(true);

            // create a 3-second timeout to reset the share button
            clearTimeout(timeoutId);
            setTimeoutId(
                setTimeout(
                    () => {
                        if (getIsMounted()) {
                            setHasShared(false);
                        }
                    },
                    3000
                )
            );
        } catch (error) {
            // eslint-disable-next-line no-console
            console.error(error.message);
        }
    }


    return (
        <Container className="rh-display-flex rh-flex-direction-column">
            <PageBehaviourButton
                data-name="edb.cc.side-by-side.share"
                size="small"
                onClick={shareURL}
                iconLocation={
                    hasShared
                        ? 'right'
                        : 'left'
                }
                iconComponent={
                    hasShared
                        ? IconCheckmark
                        : IconShare
                }
                className={classNames('rh-ml-auto rh-mb-0_75', { 'rh-fx-pop': hasShared })}
                message={(
                    <FormattedMessage
                        {...(hasShared
                            ? MESSAGES.LINK_COPIED
                            : MESSAGES.SHARE_LINK
                        )}
                    />
                )}
            />

            <Form onChange={debouncedFiltersChange}>
                <div className="filter-wrapper rh-display-flex rh-flex-wrap rh-gap-1 rh-p-1">
                    <CCSideBySideFilters
                        monthlySpendingAverage={monthlySpendingAverage}
                        monthlySpending={monthlySpending}
                    />
                </div>
            </Form>

            <ul className="side-by-side-list rh-display-flex rh-gap-1 rh-pl-0 rh-list-style-type-none">
                <For
                    each="card"
                    of={cardInfoList}
                    index="index"
                >
                    <li
                        className="list-item"
                        key={index}
                    >
                        <CreditCardFlipContainer
                            id={`compare-card-${index}`}
                            cardInfo={cardInfoList[ index ]}
                            onClear={() => handleRemoveCard(index)}
                            onSelect={(cardId) => handleCardSelection(cardId, index)}
                        />
                    </li>
                </For>
            </ul>
        </Container>
    );
}

CCSideBySide.propTypes = {
    initialCardInfos: PropTypes.arrayOf(
        PropTypes.shape(CreditCardSynopsisShape)
    ),
    province: PropTypes.oneOf(Object.values(PROVINCES)),
};


const FILTER_CHANGE_INPUT_DEBOUNCE_DELAY = 1500;

/**
 * This is a convenience wrapper for calling fetchCardInfo(), returning a Promise.
 *
 * @param {string} cardId
 * @param {string} languageCode
 * @param {string} province
 * @param {Object} monthlySpending
 * @return {Promise<CreditCardShape|null>}
 * @constructor
 */
function fetchCreditCardInfo({ cardId, languageCode, province, monthlySpending }) {
    return fetchCardInfo({
        id: cardId,
        languageCode,
        province,
        cardFinderUserAmounts: {
            ...DEFAULT_CARD_PREFERENCES,
            monthlySpending: monthlySpending,
            monthlySpendingAverage: getSumOfObjectValues(monthlySpending),
        },
    });
}

function CCSideBySideFilters({ monthlySpendingAverage, monthlySpending }) {
    return (
        <>
            <div className="monthly-average rh-display-flex">
                <QuestionCompact
                    className="compact-override-margin"
                    labelMessage={(
                        <FormattedMessage
                            {...COMMON_MESSAGES.spendingMonthly}
                        />
                    )}
                    field={{ name: 'monthlySpendingAverage', value: monthlySpendingAverage }}
                    type="currency"
                />
                <div className="vertical-divider rh-ml-1" />
            </div>

            <div className="category-wrapper rh-display-flex rh-flex-wrap rh-gap-1">
                <div className="categories rh-display-flex rh-gap-1">
                    <QuestionCompact
                        className="category-input compact-override-margin"
                        labelMessage={(
                            <FormattedMessage
                                {...COMMON_MESSAGES.spendingRestaurants}
                            />
                        )}
                        field={{ name: 'monthlySpending.restaurants', value: monthlySpending.restaurants }}
                        type="currency"
                    />
                    <QuestionCompact
                        className="category-input compact-override-margin"
                        labelMessage={(
                            <FormattedMessage
                                {...COMMON_MESSAGES.spendingGroceries}
                            />
                        )}
                        field={{ name: 'monthlySpending.groceries', value: monthlySpending.groceries }}
                        type="currency"
                    />
                    <QuestionCompact
                        className="category-input compact-override-margin"
                        labelMessage={(
                            <FormattedMessage
                                {...COMMON_MESSAGES.spendingBills}
                            />
                        )}
                        field={{ name: 'monthlySpending.bills', value: monthlySpending.bills }}
                        type="currency"
                    />
                    <QuestionCompact
                        className="category-input compact-override-margin"
                        labelMessage={(
                            <FormattedMessage
                                {...COMMON_MESSAGES.spendingGas}
                            />
                        )}
                        field={{ name: 'monthlySpending.gas', value: monthlySpending.gas }}
                        type="currency"
                    />
                </div>

                <div className="categories rh-display-flex rh-gap-1">
                    <QuestionCompact
                        className="category-input compact-override-margin"
                        labelMessage={(
                            <FormattedMessage
                                {...COMMON_MESSAGES.spendingTravel}
                            />
                        )}
                        field={{ name: 'monthlySpending.travel', value: monthlySpending.travel }}
                        type="currency"
                    />
                    <QuestionCompact
                        className="category-input compact-override-margin"
                        labelMessage={(
                            <FormattedMessage
                                {...COMMON_MESSAGES.spendingEntertainment}
                            />
                        )}
                        field={{ name: 'monthlySpending.entertainment', value: monthlySpending.entertainment }}
                        type="currency"
                    />
                    <QuestionCompact
                        className="category-input compact-override-margin"
                        labelMessage={(
                            <FormattedMessage
                                {...COMMON_MESSAGES.spendingPharmacy}
                            />
                        )}
                        field={{ name: 'monthlySpending.pharmacy', value: monthlySpending.pharmacy }}
                        type="currency"
                    />
                    <QuestionCompact
                        className="category-input compact-override-margin"
                        labelMessage={(
                            <FormattedMessage
                                {...COMMON_MESSAGES.spendingOther}
                            />
                        )}
                        field={{ name: 'monthlySpending.other', value: monthlySpending.other }}
                        type="currency"
                    />
                </div>
            </div>
        </>
    );
}

CCSideBySideFilters.propTypes = {
    monthlySpendingAverage: PropTypes.number.isRequired,
    monthlySpending: PropTypes.shape({
        gas: PropTypes.number.isRequired,
        restaurants: PropTypes.number.isRequired,
        entertainment: PropTypes.number.isRequired,
        bills: PropTypes.number.isRequired,
        groceries: PropTypes.number.isRequired,
        pharmacy: PropTypes.number.isRequired,
        travel: PropTypes.number.isRequired,
        other: PropTypes.number.isRequired,
    }).isRequired,
};


const Container = styled.div`
    /* override margins inherent within QuestionCompact */
    .compact-override-margin {
        margin: 0;
    }

    .filter-wrapper {
        flex: 1 1 570px;
        background-color: ${rgba(Colours.STONE_LIGHT, 0.7)};

        .monthly-average {
            flex: 1 0 11rem;
            max-width: 15rem;
        }

        .vertical-divider {
            height: 4rem;
            border-right: 1px solid ${Colours.STONE_DARK};
        }

        .category-wrapper {
            flex: 1 1 500px;

            > .categories {
                flex: 1 1 390px;
                max-width: 930px;

                .category-input {
                    flex: 1 1 6.5rem;
                    min-width: 7.5rem;
                }
            }
        }
    }

    // not yet a global style, but maybe some day?
    .rh-fx-pop {
        animation-duration: 0.5s;
        animation-name: rh-fx-pop;
        animation-timing-function: cubic-bezier(0.26, 0.53, 0.74, 1.48);
    }

    @keyframes rh-fx-pop {
        0% {
            opacity: 0;
            transform: scale(1.3, 1.3);
        }

        100% {
            opacity: 1;
            transform: scale(1, 1);
        }
    }

    > .side-by-side-list {
        > .list-item {
            width: 33%;
            min-width: 275px;
        }
    }
`;

const MESSAGES = defineMessages({
    SHARE_LINK: {
        id: 'edb.cc.side-by-side.share-link',
        defaultMessage: '<strong>share with link</strong>',
    },
    LINK_COPIED: {
        id: 'edb.cc.side-by-side.link-copied',
        defaultMessage: '<strong>link copied!</strong>',
    },
});

export default observer(CCSideBySide);
